import type { TypedFormBuilderInterface } from '@/services/forms/baseForm/typedFormBuilder/types'
import type { ColorPaletteOption } from '@/components/shared/color/types'
import type {
  WebWidgetChannelType,
  WebWidgetDetailsAppearance,
  WebWidgetDetailsSmsChannelSettingsUi,
  WebWidgetDetailsCallChannelSettingsUi,
  WebWidgetDetailsTargetCountries,
  WebWidgetDetailsPageUrls,
  WebWidgetDetailsScheduleTimeType,
  WebWidgetDetailsChatChannelSettingsUi,
  WebWidgetDetailsWhatsappChannelSettingsUi,
  WebWidgetDetailsEmailChannelSettingsUi,
  WebWidgetDetailsFacebookChannelSettingsUi,
  WebWidgetDetailsInstagramChannelSettingsUi,
} from '@/data/models/domain/messages/settings/webWidget/types'
import type {
  CountrySenderPhoneValue,
  ImageFileFieldValue,
  SelectSearchWithCustomValue,
  SelectSenderPhoneValue,
  WeekDaysTimeRangeList,
} from '@/services/forms/types'
import type { DeviceType } from '@/services/types'

export enum WebWidgetFormFields {
  name = 'name',
}

export enum CopyWebWidgetFormFields {
  name = 'name',
}

export enum SendWebWidgetInstructionsFormFields {
  email = 'email',
}

export type WebWidgetFormSchema = {
  [WebWidgetFormFields.name]: string
}

export type CopyWebWidgetFormSchema = {
  [CopyWebWidgetFormFields.name]: string
}

export type SendWebWidgetInstructionsFormSchema = {
  [SendWebWidgetInstructionsFormFields.email]: string
}

export const WebWidgetColors = ['blue', 'orange', 'green', 'red', 'cyan', 'violet', 'purpur', 'brown'] as const
export const WebWidgetColorsPaletteSolid = WebWidgetColors.map((color) => ({
  color,
  outline: false,
  gradient: false,
})) satisfies ColorPaletteOption[]

export const WebWidgetColorsPaletteGradient = WebWidgetColors.map((color) => ({
  color,
  outline: false,
  gradient: true,
})) satisfies ColorPaletteOption[]

export type WebWidgetEmailChannelForm = {
  isActive: boolean
  inbox: SelectSearchWithCustomValue
  uiSettings: WebWidgetDetailsEmailChannelSettingsUi
  fieldSettings: {
    name: boolean
  }
}

export type WebWidgetSmsChannelForm = {
  isActive: boolean
  uiSettings: WebWidgetDetailsSmsChannelSettingsUi
  globalNumber: SelectSenderPhoneValue
  numbers: CountrySenderPhoneValue[]
}

export type WebWidgetCallChannelForm = {
  isActive: boolean
  uiSettings: WebWidgetDetailsCallChannelSettingsUi
  globalNumber: SelectSenderPhoneValue
  numbers: CountrySenderPhoneValue[]
}

export type WebWidgetWhatsappChannelForm = {
  isActive: boolean
  uiSettings: WebWidgetDetailsWhatsappChannelSettingsUi
  globalNumber: SelectSenderPhoneValue
}

export type WebWidgetFacebookChannelForm = {
  isActive: boolean
  uiSettings: WebWidgetDetailsFacebookChannelSettingsUi
  facebookPage: SelectSearchWithCustomValue
}

export type WebWidgetInstagramChannelForm = {
  isActive: boolean
  uiSettings: WebWidgetDetailsInstagramChannelSettingsUi
  instagramAccount: SelectSearchWithCustomValue
}

export type WebWidgetChatChannelForm = {
  isActive: boolean
  uiSettings: WebWidgetDetailsChatChannelSettingsUi
  fieldSettings: {
    name: boolean
  }
  listId: string
  queue: {
    autoReplyMessage: string
    rejectTimeoutInMinutes: number
    rejectMessage: string
  }
  isSatisfactionSurveyShown: boolean
  isHidden: boolean
}

export type WebWidgetDetailsAppearanceFormSchema = WebWidgetDetailsAppearance
export type WebWidgetDetailsChannelsFormSchema = {
  [WebWidgetChannelType.EMAIL]: WebWidgetEmailChannelForm
  [WebWidgetChannelType.SMS]: WebWidgetSmsChannelForm
  [WebWidgetChannelType.CALL]: WebWidgetCallChannelForm
  [WebWidgetChannelType.CHAT]: WebWidgetChatChannelForm
  [WebWidgetChannelType.WHATSAPP]: WebWidgetWhatsappChannelForm
  [WebWidgetChannelType.FACEBOOK]: WebWidgetFacebookChannelForm
  [WebWidgetChannelType.INSTAGRAM]: WebWidgetInstagramChannelForm
}
export type WebWidgetDetailsChannelSortingFormSchema = WebWidgetChannelType[]

export type WebWidgetDetailsTargetingFormSchema = {
  countries: WebWidgetDetailsTargetCountries
  schedule: {
    timeType: WebWidgetDetailsScheduleTimeType
    timezoneId: string
    timeParams: WeekDaysTimeRangeList[]
  }
  pageUrls: WebWidgetDetailsPageUrls
  deviceType: DeviceType
}

export type WebWidgetDetailsFormSchema = {
  appearance: WebWidgetDetailsAppearanceFormSchema
  channels: WebWidgetDetailsChannelsFormSchema
  channelSorting: WebWidgetDetailsChannelSortingFormSchema
  target: WebWidgetDetailsTargetingFormSchema
  logo: ImageFileFieldValue
}

export const webWidgetDetailsStepVariant = [
  'appearance',
  'appearance/colorTheme',
  'appearance/branding',
  'appearance/minimizedState',
  'appearance/welcomeScreen',
  'channels',
  'channels/email',
  'channels/email/welcome',
  'channels/email/formFields',
  'channels/email/success',
  'channels/email/associatedEmail',
  'channels/sms',
  'channels/sms/welcome',
  'channels/sms/messageUs',
  'channels/sms/numbersAndCountries',
  'channels/call',
  'channels/call/welcome',
  'channels/call/callUs',
  'channels/call/numbersAndCountries',
  'channels/chat',
  'channels/chat/welcome',
  'channels/chat/formFields',
  'channels/chat/queue',
  'channels/chat/survey',
  'channels/chat/offline',
  'channels/chat/additionalSettings',
  'channels/whatsapp',
  'channels/whatsapp/welcome',
  'channels/whatsapp/messageUs',
  'channels/whatsapp/number',
  'channels/facebook',
  'channels/facebook/welcome',
  'channels/facebook/messageUs',
  'channels/facebook/facebookPage',
  'channels/instagram',
  'channels/instagram/welcome',
  'channels/instagram/messageUs',
  'channels/instagram/instagramAccount',
  'targeting',
  'targeting/countries',
  'targeting/schedule',
  'targeting/pageUrls',
  'targeting/deviceType',
  'code',
] as const
export type WebWidgetDetailsStep = (typeof webWidgetDetailsStepVariant)[number]

export type WebWidgetDetailsFormBuilder = TypedFormBuilderInterface<WebWidgetDetailsFormSchema>

// Helper type for creating path of object fields
type RecursivePathOfObject<
  T,
  Depth extends number,
  CurrentDepth extends number[] = [],
> = CurrentDepth['length'] extends Depth
  ? never
  : T extends object
    ? {
        [K in keyof T]: K extends string
          ? T[K] extends any[]
            ? `${K}` | `${K}.${RecursivePathOfObject<T[K][number], Depth, [...CurrentDepth, 1]>}`
            : T[K] extends object
              ? `${K}` | `${K}.${RecursivePathOfObject<T[K], Depth, [...CurrentDepth, 1]>}`
              : `${K}`
          : never
      }[keyof T]
    : never

export type WebWidgetDetailsFormSchemaPaths = RecursivePathOfObject<WebWidgetDetailsFormSchema, 4>

export type WebWidgetDetailsWarningFieldGroup = WebWidgetDetailsFormSchemaPaths
